import { useState } from "react";
import { makeStyles, TextareaAutosize } from "@material-ui/core";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Alert from "@material-ui/lab/Alert";
import Modal from "@material-ui/core/Modal";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { InputField } from "../../../../../components/input-field/InputField";
import { InputButton } from "../../../../../components/input-button/InputButton";
import { BranchManagerService } from "../../../../../services/BranchManagerService";
import { InputDate } from "../../../../../components/input-date/InputDate";
import moment from "moment";
import { IoIosCalendar } from "react-icons/io";
import { ImageChooser } from "../../../../../components/image-chooser/ImageChooser";
import { Box, InputAdornment, FormHelperText } from "@material-ui/core";

import {
  FILE_MAX_SIZE,
  FILE_MAX_SIZE_STR,
} from "../../../../../settings/constants";

import { base64, validateFile } from "../../../../../common/Hooks";
import { AnnouncementsAttributes } from "../../../../../attributes/announcements-attributes";

interface AddAnnouncementProps {
  branchId: number;
  isOpened: boolean;
  closeCallback: () => void;
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
  },
  modalBody: {
    width: 416,
    padding: 10,
    border: "none",
  },
  imageDescription: {
    marginTop: 10,
  },

  antPickerDropdown: {
    zIndex: 100000,
  },
}));

export const NewAnnouncement = (props: AddAnnouncementProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const { formatMessage } = useIntl();
  const { branchId, isOpened, closeCallback } = props;
  const [error, setError] = useState(false);
  const [succeed, setSucceed] = useState(false);

  const handleAttachmentSelection = (name: string, file: File) => {
    if (file !== null) {
      base64(file, (result) => {
        formik.values[name] = result;
      });
    } else {
      formik.values[name] = null;
    }
    formik.setErrors({ ...formik.errors, [name]: undefined });
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      start_date: "",
      end_date: "",
      images: null,
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseEnterAnnounceTitle" })
      ),
      description: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseEnterAnnounceDescription" })
      ),
      start_date: Yup.date().required(
        formatMessage({ id: "sharedScreen.pleaseEnterAnnounceStartDate" })
      ),
      end_date: Yup.date().required(
        formatMessage({ id: "sharedScreen.pleaseEnterAnnounceEndDate" })
      ),
      images: Yup.mixed()
        .required(intl.formatMessage({ id: "error.selectValidPhoto" }))
        .test(
          "fileSize",
          intl.formatMessage(
            { id: "error.tooLargePicture" },
            { max: FILE_MAX_SIZE_STR }
          ),
          (value) => {
            return value !== null && validateFile(value, FILE_MAX_SIZE);
          }
        ),
    }),
    onSubmit: async (
      values: AnnouncementsAttributes,
      { setSubmitting, resetForm }: FormikHelpers<any>
    ) => {
      setError(false);
      try {
        const announcementResult = await BranchManagerService.addAnnouncement(
          branchId,
          formik.values
        );
        if (
          announcementResult.isSuccess &&
          announcementResult.data.code === 100
        ) {
          resetForm({});
          setSucceed(true);
          setTimeout(() => {
            setSucceed(false);
          }, 2000);
        } else {
          setError(true);
        }
      } catch (error) {
        setError(true);
      }
      setSubmitting(false);
    },
  });

  const handleDateChange = (name: string, dateString: string) => {
    formik.setValues({ ...formik.values, [name]: dateString });
  };

  return (
    <Modal className={classes.modal} open={isOpened} onClose={closeCallback}>
      <Paper className={classes.modalBody}>
        <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          <Grid container spacing={2}>
            <Grid
              item
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              xs={12}
              sm={12}
              lg={12}
            >
              <Typography
                className={classes.modalTitle}
                variant="h4"
                component="h4"
              >
                <FormattedMessage id="sharedScreen.newAnnouncement" />
              </Typography>
              <IconButton edge="end" onClick={closeCallback}>
                <HighlightOffIcon fontSize="large" />
              </IconButton>
            </Grid>
            {error && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="error">
                  <FormattedMessage id="sharedScreen.thereWasAProblemCreatingAnnouncement" />
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={12} lg={12}>
              <InputField
                value={formik.values.title}
                fullWidth
                //label={formatMessage({ id: "sharedScreen.announcement.title" })}
                placeholder={formatMessage({
                  id: "sharedScreen.announcement.title",
                })}
                name="title"
                onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />

              <TextareaAutosize
                className={classes.imageDescription}
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                aria-label={formatMessage({ id: "sharedScreen.announcement.description" })}
                //minRows={3}
                placeholder={formatMessage({ id: "sharedScreen.announcement.description" })}
                style={{ width: "100%", height:50 }}
              />

              <InputDate
                minDate={moment()
                  .subtract("day", 1)
                  .format("YYYY-MM-DD")}
                onDateChange={(name, date) => handleDateChange(name, date)}
                inputProps={{
                  disabled: formik.isSubmitting,
                  fullWidth: true,
                  id: "start_date",
                  name: "start_date",
                  placeholder: intl.formatMessage({
                    id: "sharedScreen.announcement.startDate",
                  }),
                  endAdornment: (
                    <InputAdornment position="start">
                      <IoIosCalendar />
                    </InputAdornment>
                  ),
                  value: formik.values.start_date,
                  error:
                    Boolean(formik.touched.start_date) &&
                    Boolean(formik.errors.start_date),
                  helperText:
                    formik.touched.start_date && formik.errors.start_date,
                }}
                pickerProps={{
                  format: "YYYY-MM-DD",
                  disabled: false,
                  showToday: false,
                  showTime: true,
                }}
              />

              <InputDate
                minDate={moment().format("YYYY-MM-DD")}
                onDateChange={(name, date) => handleDateChange(name, date)}
                inputProps={{
                  disabled: formik.isSubmitting,
                  fullWidth: true,
                  id: "end_date",
                  name: "end_date",
                  placeholder: intl.formatMessage({
                    id: "sharedScreen.announcement.endDate",
                  }),
                  endAdornment: (
                    <InputAdornment position="start">
                      <IoIosCalendar />
                    </InputAdornment>
                  ),
                  value: formik.values.end_date,
                  error:
                    Boolean(formik.touched.end_date) &&
                    Boolean(formik.errors.end_date),
                  helperText: formik.touched.end_date && formik.errors.end_date,
                }}
                pickerProps={{
                  format: "YYYY-MM-DD",
                  disabled: false,
                  showToday: false,
                  showTime: true,
                }}
              />

              <InputField
                value={formik.values.discount}
                type="number"
                fullWidth
                placeholder={formatMessage({
                  id: "sharedScreen.announcement.discount",
                })}
                name="discount"
                onChange={formik.handleChange}
                error={
                  formik.touched.discount && Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.discount
                }
              />
              <br />
              <br />

              <Box mb={2}>
                <ImageChooser
                  variant="square"
                  onImageSelected={(file) =>
                    handleAttachmentSelection("images", file)
                  }
                  label={intl.formatMessage({
                    id: "sharedScreen.announcementImage",
                  })}
                />
                {formik.touched.images && formik.errors.images && (
                  <FormHelperText className="errorWrapper">
                    {formik.errors.images}
                  </FormHelperText>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} lg={12} container direction="row">
              <InputButton
                disabled={formik.isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
              >
                <FormattedMessage id="sharedScreen.create" />
                {formik.isSubmitting && <CircularProgress size={30} />}
              </InputButton>
            </Grid>
            {succeed && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="success">
                  <FormattedMessage id="sharedScreen.announceCreatedSuccessfully" />
                </Alert>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </Modal>
  );
};
